import type { SubType } from './product';
import type { Role } from './role';

// 邀請狀態類型
export type InviteStatus = 'pending' | 'accepted' | 'rejected' | 'expired';

export interface OrganizationsMember {
  avatar: string;
  create_at: number;
  email: string;
  id: number;
  is_access: boolean;
  is_admin: boolean;
  is_current: boolean;
  is_invited: boolean;
  nickname: string;
  organizations_name: string;
  organizations_uuid: string;
  roles: Role[];
  roles_uuid: string;
  token: string;
  uid: number;
  update_at?: number;
  // 新增邀請相關欄位
  invite_status?: InviteStatus;
  invite_created_at?: number;
  invite_expired_at?: number;
  inviter_uid?: number;
  inviter_deleted?: boolean;
  invitee_deleted?: boolean;
  rejected_at?: number;
}

export interface JoinOrgRequest {
  organization_uuid: string;
}

export interface Organization {
  id: number;
  name: string;
  organizations_uuid: string;
  my_roles: Role[];
  is_owner: boolean;
  is_access: boolean;
  token: string;
  create_at: number;
  update_at: number;
  // 新增邀請相關欄位
  invite_status?: InviteStatus;
  invite_created_at?: number;
  invite_expired_at?: number;
  inviter_uid?: number;
  inviter_deleted?: boolean;
  invitee_deleted?: boolean;
  rejected_at?: number;
}

export interface OrganizationsProduct {
  tier_level?: number;
  billing_status?: 'active' | 'past_due';
  next_retry_at?: number;
  downgrade_at?: number;
  managed_by?: 'self' | 'admin';
  pending_3ds?: boolean;
  account_balance?: number;
  /** Current payment-service contract is USD; accept currency when supplied. */
  currency?: string;
  allowed_actions?: import('./subscription').SubscriptionAction[];
  pending_change?: import('./subscription').PendingPlanChange | null;
  expired_at: number;
  file_storage: number;
  free_mxc: number;
  id: number;
  is_current: boolean;
  organizations_uuid: string;
  product_json: string;
  product_unique_name: string;
  product_uuid: string;
  scheduled_task_limit: number;
  /** 透過平台部署的網站數上限；後端功能尚未實作 */
  deployment_limit: number;
  start_at: number;
  /** Free 檔為空字串 */
  sub_type: SubType | '';
  subscription_id: string;
  update_at: number;
}

export interface ChangeOrganizationResponse {
  id: number;
  uid: number;
  device_id: string;
  token: string;
  refresh_token: string;
  expires_in: number;
  re_expires_in: number;
  create_at: number;
  update_at: number;
}

export interface TransferOwnershipRequest {
  organization_uuid: string;
  target_user_id: number;
}

/**
 * DELETE /organizations?uuid= 在後端重發 token 失敗時走的分支。
 * 此時手上的 token 仍指向已被刪除的組織，呼叫端必須強制重新登入。
 */
export interface DeleteOrganizationRefreshNeeded {
  deleted: true;
  refresh_needed: true;
}

/** 正常路徑與 leave 同契約（回新 token）；重發失敗時改回 refresh_needed */
export type DeleteOrganizationResponse =
  | ChangeOrganizationResponse
  | DeleteOrganizationRefreshNeeded;

export function isDeleteOrganizationRefreshNeeded(
  response: DeleteOrganizationResponse
): response is DeleteOrganizationRefreshNeeded {
  return (response as DeleteOrganizationRefreshNeeded).refresh_needed === true;
}

/**
 * 409 —— 刪除有餘額的組織時，後端要求前端把實查餘額原樣送回當作知情確認
 * （BE Momentx-AI-Business#883 / PR #888）。純前端警語擋不住 stale client，
 * 所以確認這件事被搬到伺服器端驗。
 *
 * 刻意不進 constants/apiErrorCodes.ts：那張表是給全域攔截器出 toast 用的，
 * 而這個碼要就地把新金額餵回彈窗、讓使用者再確認一次，不是報錯了事。
 */
export const BALANCE_ACK_REQUIRED = 'balance_ack_required';

/**
 * 從 409 錯誤取出後端實查到的餘額（`error.data.current_balance`）。
 * 不是這個碼、或欄位不是可用的數字就回 null —— 呼叫端據此決定要重新確認還是照常報錯。
 */
export function extractBalanceAckRequired(error: unknown): number | null {
  const err = error as { code?: string; details?: unknown } | null;
  if (err?.code !== BALANCE_ACK_REQUIRED) {
    return null;
  }
  const data = (err.details as { data?: { current_balance?: unknown } } | undefined)?.data;
  const balance = data?.current_balance;
  return typeof balance === 'number' && Number.isFinite(balance) ? balance : null;
}
